import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDeviceDetail,
  getDeviceFloors,
  getDeviceLogs,
  reassignDeviceOwner,
  remoteAction,
  replaceDeviceFloors,
  setWarrantyRemark,
  updateWarranty,
} from '../api/admin.js';
import type {
  AdminDeviceDetailResponse,
  AdminRemoteAction,
  WarrantyClaimState,
} from '@m8-app/contracts/rest/admin.js';
import type { Floor } from '@m8-app/contracts/rest/floors.js';

type Detail = AdminDeviceDetailResponse;

type Tab = 'overview' | 'users' | 'floors' | 'logs' | 'warranty' | 'service' | 'remote';

function buildTabs(isLift: boolean): { key: Tab; label: string }[] {
  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'users',    label: 'Users' },
  ];
  if (isLift) tabs.push({ key: 'floors', label: 'Floors' });
  tabs.push(
    { key: 'logs',     label: 'Logs' },
    { key: 'warranty', label: 'Warranty' },
    { key: 'service',  label: 'Service History' },
    { key: 'remote',   label: 'Remote Actions' },
  );
  return tabs;
}

export function DeviceDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>('overview');

  const { data, isLoading } = useQuery({
    queryKey: ['adminDevice', id],
    queryFn: () => getDeviceDetail(id),
    enabled: !!id,
  });

  if (isLoading || !data) return <div className="p-8 text-sm text-gray-500">Loading…</div>;

  const d = data.device;
  const isLift = d.typeName?.toLowerCase() === 'lift';
  const TABS = buildTabs(isLift);

  return (
    <div className="p-8">
      <Link to="/devices" className="text-sm text-indigo-600 hover:underline">← Devices</Link>
      <h2 className="text-2xl font-bold text-gray-900 mt-2">{d.name ?? d.serialNumber}</h2>
      <p className="text-sm text-gray-500 font-mono">{d.serialNumber}</p>

      <div className="flex gap-1 my-6 bg-gray-100 rounded-lg p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview data={data} />}
      {tab === 'users'    && <Users   data={data} />}
      {tab === 'floors'   && isLift && <Floors deviceId={id} />}
      {tab === 'logs'     && <Logs    deviceId={id} />}
      {tab === 'warranty' && <Warranty data={data} />}
      {tab === 'service'  && <ServiceHistory data={data} />}
      {tab === 'remote'   && <RemoteActions deviceId={id} />}
    </div>
  );
}

function formatWarranty(iso: string): string {
  const expires = new Date(iso);
  const now = new Date();
  const daysLeft = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const label = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(expires);
  if (daysLeft < 0) return `${label} (expired)`;
  if (daysLeft <= 30) return `${label} (${daysLeft}d left)`;
  return label;
}

function Overview({ data }: { data: Detail }) {
  const d = data.device;
  return (
    <div className="grid grid-cols-2 gap-6 max-w-3xl">
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-2 text-sm">
        <Row k="Type"     v={d.typeName ?? '—'} />
        <Row k="Claimed"    v={d.claimStatus === 'claimed' ? 'Claimed' : 'Unclaimed'} />
        <Row k="Registered" v={d.sellerRegistered ? 'Registered' : 'Not Registered'} />
        <Row k="Presence" v={d.presence ?? '—'} />
        <Row k="Firmware" v={d.activeFirmwareVersion ?? '—'} />
        <Row k="Location" v={d.location ?? '—'} />
        <Row k="Created"  v={d.createdAt.slice(0, 10)} />
        <Row k="Warranty" v={d.warrantyExpiresAt ? formatWarranty(d.warrantyExpiresAt) : '—'} />
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-2 text-sm">
        <h3 className="font-semibold text-gray-900 mb-2">Owner</h3>
        {data.owner ? (
          <>
            <Row k="Mobile" v={data.owner.mobile} />
            <Row k="Status" v={data.owner.status} />
          </>
        ) : <p className="text-gray-400 italic">Not registered</p>}
        <ReassignOwner deviceId={d.id} currentMobile={data.owner?.mobile ?? null} />
      </div>
      <div className="col-span-2 bg-white rounded-xl border border-gray-200 p-5 text-sm">
        <h3 className="font-semibold text-gray-900 mb-2">Remark</h3>
        <p className="text-gray-700">{d.remark || <span className="italic text-gray-400">No remark</span>}</p>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="flex justify-between"><span className="text-gray-500">{k}</span><span className="text-gray-900">{v}</span></div>;
}

// Staff recovery path for Main User assignment — moves ownership to a mobile
// number (wrong number at assignment, unreachable owner). super_admin/admin
// only; the backend rejects the staff tier.
function ReassignOwner({ deviceId, currentMobile }: { deviceId: string; currentMobile: string | null }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => reassignDeviceOwner(deviceId, mobile.startsWith('+') ? mobile : `+91${mobile}`),
    onSuccess: () => {
      setOpen(false);
      setMobile('');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['adminDevice', deviceId] });
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Reassignment failed';
      setError(msg);
    },
  });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800"
      >
        Reassign owner…
      </button>
    );
  }

  return (
    <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
      <p className="text-xs text-gray-500">
        The entered mobile becomes this device&apos;s Main User
        {currentMobile ? <> (replacing <span className="font-mono">{currentMobile}</span>)</> : null}.
        Takes effect from their first login if they have no account yet.
      </p>
      <input
        value={mobile}
        onChange={(e) => setMobile(e.target.value.trim())}
        placeholder="+91XXXXXXXXXX"
        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-mono"
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || mobile.replace(/\D/g, '').length < 10}
          className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {mutation.isPending ? 'Reassigning…' : 'Confirm reassignment'}
        </button>
        <button
          onClick={() => { setOpen(false); setError(null); }}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Users({ data }: { data: Detail }) {
  const reg = data.sellerRegistration;
  const hasAnyUser = data.owner || reg;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-w-3xl">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-left">
          <tr>
            <th className="px-4 py-3 font-medium">Name</th>
            <th className="px-4 py-3 font-medium">Mobile</th>
            <th className="px-4 py-3 font-medium">Role</th>
            <th className="px-4 py-3 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {/* Registered customer → always Main User */}
          {reg && (
            <tr>
              <td className="px-4 py-3 font-medium text-gray-900">{reg.customerName ?? '—'}</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-700">{reg.customerMobile}</td>
              <td className="px-4 py-3">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 ring-1 ring-blue-200/60">
                  Main User
                </span>
              </td>
              <td className="px-4 py-3">
                {data.owner?.mobile === reg.customerMobile ? (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60">Claimed</span>
                ) : (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 ring-1 ring-amber-200/60">Pending claim</span>
                )}
              </td>
            </tr>
          )}
          {/* Owner if different from registered customer (e.g. override claim) */}
          {data.owner && (!reg || data.owner.mobile !== reg.customerMobile) && (
            <tr>
              <td className="px-4 py-3 font-medium text-gray-900">—</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-700">{data.owner.mobile}</td>
              <td className="px-4 py-3">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 ring-1 ring-blue-200/60">
                  Main User
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60">
                  {data.owner.status === 'active' ? 'Active' : 'Blocked'}
                </span>
              </td>
            </tr>
          )}
          {!hasAnyUser && (
            <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No users</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Floors tab (lift devices only) ────────────────────────────────────────

function Floors({ deviceId }: { deviceId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['deviceFloors', deviceId],
    queryFn: () => getDeviceFloors(deviceId),
  });

  const replaceMutation = useMutation({
    mutationFn: (
      floors: { label: string; ordinal: number; category: Floor['category']; building?: string }[],
    ) => replaceDeviceFloors(deviceId, { floors }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['deviceFloors', deviceId] });
      setEditing(false);
    },
  });

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;

  const floors = data?.floors ?? [];

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          {floors.length} floor{floors.length !== 1 ? 's' : ''} configured
        </h3>
        <button
          onClick={() => setEditing(true)}
          className="px-3 py-1.5 text-sm text-indigo-600 border border-indigo-300 rounded-lg hover:bg-indigo-50"
        >
          Edit Floors
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">#</th>
              <th className="px-4 py-3 font-medium">Label</th>
              <th className="px-4 py-3 font-medium">Building</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {floors.map((f) => (
              <tr key={f.id}>
                <td className="px-4 py-3 text-gray-400 tabular-nums">{f.ordinal}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{f.label}</td>
                <td className="px-4 py-3 text-gray-500">{f.building ?? '—'}</td>
              </tr>
            ))}
            {floors.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400">No floors configured</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <FloorsEditModal
          floors={floors}
          isPending={replaceMutation.isPending}
          {...(replaceMutation.error?.message !== undefined && { error: replaceMutation.error.message })}
          onClose={() => setEditing(false)}
          onSave={(f) => replaceMutation.mutate(f)}
        />
      )}
    </div>
  );
}

function FloorsEditModal({
  floors,
  isPending,
  error,
  onClose,
  onSave,
}: {
  floors: Floor[];
  isPending: boolean;
  error?: string;
  onClose: () => void;
  onSave: (
    floors: { label: string; ordinal: number; category: Floor['category']; building?: string }[],
  ) => void;
}) {
  // New rows default to the generic 'floor' category — this admin tool is a
  // staff fallback, not the category picker mobile Setup has; existing rows
  // keep whatever category the app already set so an admin save here can't
  // silently reset a floor from e.g. 'basement' back to 'floor'.
  const [rows, setRows] = useState(
    floors.length > 0
      ? floors.map((f) => ({
          label: f.label,
          ordinal: f.ordinal,
          category: f.category,
          building: f.building ?? '',
        }))
      : [{ label: '', ordinal: 0, category: 'floor' as Floor['category'], building: '' }],
  );

  const updateRow = (i: number, field: 'label' | 'ordinal' | 'building', value: string) => {
    setRows((prev) =>
      prev.map((r, idx) =>
        idx === i ? { ...r, [field]: field === 'ordinal' ? parseInt(value, 10) : value } : r,
      ),
    );
  };

  const addRow = () =>
    setRows((prev) => [
      ...prev,
      { label: '', ordinal: prev.length, category: 'floor' as Floor['category'], building: '' },
    ]);
  const removeRow = (i: number) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const handleSave = () => {
    onSave(rows.map((r) => ({
      label: r.label,
      ordinal: r.ordinal,
      category: r.category,
      ...(r.building ? { building: r.building } : {}),
    })));
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Floors</h3>
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input
                type="number"
                value={row.ordinal}
                onChange={(e) => updateRow(i, 'ordinal', e.target.value)}
                className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                placeholder="#"
              />
              <input
                type="text"
                value={row.label}
                onChange={(e) => updateRow(i, 'label', e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                placeholder="Label (e.g. Lobby)"
                required
              />
              <input
                type="text"
                value={row.building}
                onChange={(e) => updateRow(i, 'building', e.target.value)}
                className="w-28 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                placeholder="Building"
              />
              <button onClick={() => removeRow(i)} className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
            </div>
          ))}
        </div>
        <button
          onClick={addRow}
          className="mt-3 text-sm text-indigo-600 hover:underline"
        >
          + Add floor
        </button>
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        <div className="flex gap-3 justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
          <button
            onClick={handleSave}
            disabled={isPending}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Logs({ deviceId }: { deviceId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['deviceLogs', deviceId],
    queryFn: () => getDeviceLogs(deviceId, 100),
  });

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;

  return (
    <div className="grid grid-cols-2 gap-6 max-w-5xl">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <h3 className="px-4 py-2 text-sm font-semibold border-b">Commands</h3>
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Outcome</th>
              <th className="px-3 py-2">Latency</th>
              <th className="px-3 py-2">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data?.commands.map((c) => (
              <tr key={c.cmdId}>
                <td className="px-3 py-2">{c.type}</td>
                <td className="px-3 py-2">{c.outcome ?? '—'}</td>
                <td className="px-3 py-2">{c.latencyMs != null ? `${c.latencyMs} ms` : '—'}</td>
                <td className="px-3 py-2 text-gray-400">{c.ts.slice(0, 19).replace('T', ' ')}</td>
              </tr>
            ))}
            {data?.commands.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-400">No commands</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <h3 className="px-4 py-2 text-sm font-semibold border-b">Presence</h3>
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data?.presence.map((p, i) => (
              <tr key={i}>
                <td className="px-3 py-2">{p.state}</td>
                <td className="px-3 py-2 text-gray-400">{p.ts.slice(0, 19).replace('T', ' ')}</td>
              </tr>
            ))}
            {data?.presence.length === 0 && (
              <tr><td colSpan={2} className="px-3 py-4 text-center text-gray-400">No history</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Warranty({ data }: { data: Detail }) {
  const qc = useQueryClient();
  const states: WarrantyClaimState[] = ['open', 'in_review', 'approved', 'rejected', 'closed'];
  const { mutate: changeState } = useMutation({
    mutationFn: (v: { id: string; state: WarrantyClaimState }) => updateWarranty(v.id, { state: v.state }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adminDevice'] }),
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-w-5xl">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-left">
          <tr>
            <th className="px-4 py-3 font-medium">Description</th>
            <th className="px-4 py-3 font-medium">State</th>
            <th className="px-4 py-3 font-medium">Remark</th>
            <th className="px-4 py-3 font-medium">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.warrantyClaims.map((c) => (
            <WarrantyRow key={c.id} claim={c} states={states} onChangeState={(state) => changeState({ id: c.id, state })} />
          ))}
          {data.warrantyClaims.length === 0 && (
            <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No claims</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function WarrantyRow({
  claim, states, onChangeState,
}: {
  claim: Detail['warrantyClaims'][number];
  states: readonly WarrantyClaimState[];
  onChangeState: (s: WarrantyClaimState) => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [remark, setRemark] = useState(claim.remark ?? '');
  const { mutate: save, isPending } = useMutation({
    mutationFn: () => setWarrantyRemark(claim.id, remark),
    onSuccess: () => { setEditing(false); qc.invalidateQueries({ queryKey: ['adminDevice'] }); },
  });

  return (
    <tr>
      <td className="px-4 py-3 max-w-xs truncate">{claim.description || '—'}</td>
      <td className="px-4 py-3">
        <select value={claim.state} onChange={(e) => onChangeState(e.target.value as WarrantyClaimState)} className="text-xs border border-gray-300 rounded px-2 py-1">
          {states.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td className="px-4 py-3 max-w-[220px]">
        {editing ? (
          <div className="flex gap-1">
            <input value={remark} onChange={(e) => setRemark(e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1 flex-1"/>
            <button onClick={() => save()} disabled={isPending} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded">{isPending ? '…' : 'Save'}</button>
            <button onClick={() => { setEditing(false); setRemark(claim.remark ?? ''); }} className="text-xs px-2 py-1 text-gray-500">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="text-xs text-left text-gray-600 hover:text-indigo-600 truncate w-full">
            {claim.remark || <span className="italic text-gray-400">add remark</span>}
          </button>
        )}
      </td>
      <td className="px-4 py-3 text-gray-400">{claim.createdAt.slice(0, 10)}</td>
    </tr>
  );
}

function ServiceHistory({ data }: { data: Detail }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-w-5xl">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-left">
          <tr>
            <th className="px-4 py-3 font-medium">Category</th>
            <th className="px-4 py-3 font-medium">Description</th>
            <th className="px-4 py-3 font-medium">State</th>
            <th className="px-4 py-3 font-medium">Resolution</th>
            <th className="px-4 py-3 font-medium">Created</th>
            <th className="px-4 py-3 font-medium" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.serviceRequests.map((s) => (
            <tr key={s.id}>
              <td className="px-4 py-3">{s.category}</td>
              <td className="px-4 py-3 max-w-xs truncate">{s.description}</td>
              <td className="px-4 py-3">{s.state}</td>
              <td className="px-4 py-3">{s.resolution ?? '—'}</td>
              <td className="px-4 py-3 text-gray-400">{s.createdAt.slice(0, 10)}</td>
              <td className="px-4 py-3"><Link to={`/services/${s.id}`} className="text-xs text-indigo-600 hover:underline">Open</Link></td>
            </tr>
          ))}
          {data.serviceRequests.length === 0 && (
            <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No service history</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RemoteActions({ deviceId }: { deviceId: string }) {
  const ACTIONS: AdminRemoteAction[] = ['reboot', 'reset_wifi', 'activate', 'firmware_update'];
  const [action, setAction] = useState<AdminRemoteAction>('reboot');
  const [result, setResult] = useState<string | null>(null);
  const { mutate, isPending, error } = useMutation({
    mutationFn: () => remoteAction(deviceId, { action }),
    onSuccess: (res) => setResult(res.cmdId),
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 max-w-md space-y-3">
      <h3 className="text-sm font-semibold">Send remote command</h3>
      <select value={action} onChange={(e) => setAction(e.target.value as AdminRemoteAction)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
        {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
      </select>
      <button onClick={() => { setResult(null); mutate(); }} disabled={isPending} className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
        {isPending ? 'Sending…' : 'Send'}
      </button>
      {error && <p className="text-xs text-red-600">{(error as { message?: string }).message ?? 'Failed'}</p>}
      {result && <p className="text-xs text-green-700 bg-green-50 rounded px-2 py-1">Sent — <code className="font-mono">{result}</code></p>}
    </div>
  );
}

